<template>
  <div class="game-card" :style="style" ref="el">
    <div class="game-card__content">
      <p class="game-card__genre">{{ genre }}</p>
      <p class="game-card__name">{{ name }}</p>
      <div class="game-card__stats">
        <p class="game-stats-counter" v-for="stat in stats" :key="stat.icon">
          <i class="fa-solid game-stats-counter__icon" :class="stat.icon"/>
          <span class="game-stats-counter__value">{{ stat.value }}</span>
        </p>
      </div>
      <Button class="game-card__play-button" :href="urlToPlay" target="_blank">
        Play Now
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {StyleValue} from "vue";
import defaultBackground from "assets/images/game-cards/5.webp";

export interface GameCardProps {
  backgroundImage: string,
  genre: string,
  name: string,
  currentOnline: number,
  totalVisits: number,
  urlToPlay: string,
}

const props = defineProps<GameCardProps>();

const el = ref<HTMLDivElement | null>(null);
const {tilt, roll, source} = useParallax(el)

const stats = computed(() => [
  {
    icon: 'fa-gamepad',
    value: `${nFormatter(props.currentOnline, 2)} Online`
  },
  {
    icon: 'fa-globe',
    value: `${nFormatter(props.totalVisits, 3)} Visits`
  },
])

const style = computed<StyleValue>(() => ({
  backgroundImage: `url("${props.backgroundImage}")`,
  // backgroundPosition: `${tilt.value * 20}% ${-roll.value * 20}%`,
}))
</script>

<style scoped lang="scss">
.game-card {
  width: 384px;
  max-width: 100%;

  aspect-ratio: 1;

  background-position: center;
  background-size: 110%;
  background-repeat: no-repeat;

  border-radius: 16px;

  overflow: hidden;

  @media (min-width: $md) {
    .game-card__content {
      transform: translateY(128px);
      transition: transform 200ms ease-out;
    }

    .game-card__name {
      margin-bottom: 16px;
      transition: margin-bottom 200ms ease-out;
    }

    &:hover {
      .game-card__content {
        transform: translateY(0);
      }

      .game-card__name {
        margin-bottom: 0;
      }
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-end;
    gap: 4px;

    width: 100%;
    height: 100%;
    box-sizing: border-box;

    padding: 32px;

    background-image: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0) 0%,
        rgba(3, 10, 24, 0.75) 75%,
        rgba(3, 10, 24, 0.85) 100%
    );
  }

  &__genre {
    color: #ffffff;
    font-family: var(--font-family-main);
    font-size: 16px;
    font-weight: 400;
    white-space: nowrap;
    text-transform: capitalize;

    @media (max-width: $md) {
      font-size: 14px;
    }
  }

  &__name {
    color: #ffffff;
    font-family: var(--font-family-main);
    font-size: 32px;
    font-weight: 700;
    line-height: 1.1;
    text-transform: uppercase;

    @media (max-width: $md) {
      font-size: 28px;
    }
  }

  &__stats {
    display: flex;
    gap: 16px;

    margin-bottom: 8px;

    @media (max-width: $md) {
      gap: 4px;
      flex-direction: column;
    }
  }
}

.game-stats-counter {
  display: flex;
  align-items: center;
  gap: 8px;

  &__icon {
    color: #ffffff;
    font-family: "Font Awesome 6 Free", sans-serif;
    font-size: 16px;
    font-weight: 900;
    white-space: nowrap;
  }

  &__value {
    color: #ffffff;
    font-family: var(--font-family-main);
    font-size: 16px;
    font-weight: 500;
    white-space: nowrap;
  }
}
</style>